import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ApiFehler, type BelegDetails, type JournalBeleg, type KassenApi } from '../api';
import { aktuelleRechte } from '../bediener';
import { formatiereEuro } from '../warenkorb';
import {
  effektiveZeilen,
  kumulativerBetrag,
  negativeZeilenSperre,
  pruefeOriginal,
  type OriginalPosition,
  type OriginalPruefung,
  type Zeilenwert,
} from './berechnung';
import { deuteEingabe, istVerkaufsbeleg, stornoAusJournal } from './belegsuche';
import {
  ABSCHLUSS_GESPERRT_TEXT,
  auswahlListe,
  fehlendeAngaben,
  freigabeHinweise,
  GRUENDE,
  LEERE_ANGABE,
  LEERER_ENTWURF,
  SCHRITTE,
  STATUS_GUELTIG_MS,
  STATUS_PRUEFUNG_MS,
  systemSperre,
  setzeAngabe,
  setzeMenge,
  vorbereitungErlaubt,
  vorschau,
  ZUSTAENDE,
  zustandWaehlbar,
  type Entwurf,
  type Schritt,
  type SystemStand,
} from './entwurf';
import './retoure.css';

/**
 * Reiter „Retoure“ – Stufe 1, VORBEREITET, NICHT AKTIVIERT (Schalter `bilanx.retoureStufe1`).
 *
 * Design RT-FINAL „B + Betragsband aus C“ (Figma Section 277:10482, Rahmen RF-V01–V09): links
 * die Arbeitsfläche mit Schrittreiter, rechts Zusammenfassung mit Betragsband und große Tasten.
 *
 * Die Komponente bekommt nur lesende API-Methoden (`beleg`, `belegjournal`) – sie kann weder
 * Belege erstellen noch Zahlungen auslösen oder die Lade öffnen. Beträge sind eine Vorschau aus
 * dem Originalbeleg (gemeinsame Rechenlogik mit dem Server); der Abschluss ist sichtbar gesperrt,
 * bis RF-1/RF-2/RF-4 und E-SB-1 freigegeben sind. Retouren nur online (Fachkonzept 12).
 */
export type RetoureLeseApi = Pick<KassenApi, 'beleg' | 'belegjournal' | 'status'>;

interface Original {
  beleg: BelegDetails;
  storno: string | null;
  pruefung: OriginalPruefung;
  zeilen: Zeilenwert[];
  fehler: string[];
  positionen: OriginalPosition[];
}

function datum(iso: string): string {
  return new Date(iso).toLocaleString('de-AT', {
    timeZone: 'Europe/Vienna',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function euro(cent: number): string {
  return `${formatiereEuro(cent)} €`;
}

function satzText(satz: string): string {
  return `${satz} %`;
}

function mengeText(m: number): string {
  return Number.isInteger(m) ? String(m) : m.toLocaleString('de-AT');
}

function useOnline(vorgabe: boolean | undefined): boolean {
  const [online, setOnline] = useState(
    () => vorgabe ?? (typeof navigator === 'undefined' ? true : navigator.onLine),
  );
  useEffect(() => {
    if (vorgabe !== undefined) {
      setOnline(vorgabe);
      return;
    }
    const an = () => {
      setOnline(true);
    };
    const aus = () => {
      setOnline(false);
    };
    window.addEventListener('online', an);
    window.addEventListener('offline', aus);
    return () => {
      window.removeEventListener('online', an);
      window.removeEventListener('offline', aus);
    };
  }, [vorgabe]);
  return online;
}

export function Retoure(props: {
  api: RetoureLeseApi;
  kassenId: string;
  /** Rechte des angemeldeten Bedieners; Vorgabe aus der Sitzung (Tests setzen sie direkt). */
  rechte?: { storno: boolean };
  /** Verbindungszustand; Vorgabe `navigator.onLine` (Tests setzen ihn direkt). */
  online?: boolean;
  /**
   * M2: Sperre aus dem Zustand der Kasse (App.tsx: offline gepufferte Belege, Verkaufsstopp,
   * ungeklärter Verkauf, Anmeldung) – `kassenSperreFuerRetoure`. null = keine.
   */
  kassenSperre?: string | null;
}) {
  const { api, kassenId } = props;
  const rechte = props.rechte ?? aktuelleRechte(sessionStorage, kassenId);
  const online = useOnline(props.online);
  const darf = vorbereitungErlaubt(rechte);

  // M2: Kassenstatus (Ausfall der Sicherheitseinrichtung, außer Betrieb) – beim Öffnen, danach
  // regelmäßig und vor jedem Schritt neu; solange unbekannt, ist alles gesperrt.
  const [system, setSystem] = useState<SystemStand>({ art: 'pruefung' });
  /**
   * Codex Runde 2 (M2a): monotone Kennung je Statusabruf. Nur die Antwort des zuletzt
   * begonnenen Abrufs zählt; eine überholte (verzögerte) Antwort wird verworfen und kann eine
   * neuere Sperre nie aufheben. Jeder neue Abruf setzt den Stand zuerst auf „wird geprüft“.
   */
  const abruf = useRef(0);
  /** Codex Runde 2 (M2b): Eine Schrittprüfung läuft – alle Aktionen gesperrt. */
  const [pruefeSchritt, setPruefeSchritt] = useState(false);
  /**
   * Laufende Schrittprüfung (Kennung) oder null. Synchron gesetzt gegen Doppeltipp; Codex
   * Runde 3 (2): Wird die Prüfung überholt (neuer Abruf, offline), wird sie hier abgebrochen
   * und die Sperre sofort freigegeben – ihre späte Antwort bleibt wirkungslos.
   */
  const schrittPruefung = useRef<number | null>(null);
  const schrittKennung = useRef(0);
  const brichSchrittPruefungAb = useCallback(() => {
    if (schrittPruefung.current !== null) {
      schrittPruefung.current = null;
      setPruefeSchritt(false);
    }
  }, []);
  const statusPruefen = useCallback(
    async (fuerSchritt = false): Promise<SystemStand | null> => {
      if (!fuerSchritt) {
        brichSchrittPruefungAb();
      }
      const kennung = ++abruf.current;
      setSystem({ art: 'pruefung' });
      let neu: SystemStand;
      try {
        neu = { art: 'ok', status: await api.status(kassenId), geprueftAm: Date.now() };
      } catch {
        neu = { art: 'fehler' };
      }
      if (kennung !== abruf.current) {
        return null; // überholt
      }
      setSystem(neu);
      return neu;
    },
    [api, kassenId, brichSchrittPruefungAb],
  );
  useEffect(() => {
    if (!online) {
      return;
    }
    void statusPruefen();
    const t = setInterval(() => {
      void statusPruefen();
    }, STATUS_PRUEFUNG_MS);
    return () => {
      clearInterval(t);
      abruf.current++; // laufende Abrufe gelten als überholt (offline, Wechsel der Kasse)
      brichSchrittPruefungAb();
    };
  }, [online, statusPruefen, brichSchrittPruefungAb]);
  // Gültigkeit des letzten Stands begrenzen: nach Ablauf neu darstellen (dann „veraltet“)
  const [uhr, setUhr] = useState(() => Date.now());
  useEffect(() => {
    if (system.art !== 'ok') {
      return;
    }
    const t = setTimeout(
      () => {
        setUhr(Date.now());
      },
      Math.max(0, system.geprueftAm + STATUS_GUELTIG_MS - Date.now()) + 1,
    );
    return () => {
      clearTimeout(t);
    };
  }, [system]);
  const kassenSperre = props.kassenSperre ?? null;
  const sperrgrund = systemSperre(system, kassenSperre, Math.max(uhr, Date.now()));
  const gesperrt = !online || !darf || sperrgrund !== null || pruefeSchritt;

  const [schritt, setSchritt] = useState<Schritt>('beleg');
  const [eingabe, setEingabe] = useState('');
  const [suchMeldung, setSuchMeldung] = useState<string | null>(null);
  const [treffer, setTreffer] = useState<JournalBeleg[] | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [original, setOriginal] = useState<Original | null>(null);
  const [entwurf, setEntwurf] = useState<Entwurf>(LEERER_ENTWURF);
  const [storniHeute, setStorniHeute] = useState<JournalBeleg[] | null>(null);

  const ueberschrift = useRef<HTMLHeadingElement>(null);
  const suchfeld = useRef<HTMLInputElement>(null);
  const ersterSchritt = useRef(true);
  useEffect(() => {
    // Fokusführung: nach jedem Schrittwechsel auf die Überschrift der Arbeitsfläche
    if (ersterSchritt.current) {
      ersterSchritt.current = false;
      return;
    }
    ueberschrift.current?.focus();
  }, [schritt]);

  // Übersicht: bisherige Storni dieser Kasse (bestehender Weg), Retourenbelege gibt es noch nicht
  useEffect(() => {
    if (!online) {
      return;
    }
    let aktiv = true;
    api
      .belegjournal(kassenId, { suche: 'ST-', limit: 20 })
      .then((r) => {
        if (aktiv) {
          setStorniHeute(r.belege.filter((b) => b.belegtyp === 'STORNO').slice(0, 5));
        }
      })
      .catch(() => {
        if (aktiv) {
          setStorniHeute(null);
        }
      });
    return () => {
      aktiv = false;
    };
  }, [api, kassenId, online]);

  const bisherZurueck = useMemo(() => {
    const m = new Map<number, number>();
    if (original !== null && original.storno !== null) {
      for (const z of original.zeilen) {
        m.set(z.index, z.menge);
      }
    }
    return m;
  }, [original]);

  const ergebnis = useMemo(
    () =>
      original === null || entwurf.auswahl.size === 0
        ? null
        : vorschau(original.positionen, entwurf, bisherZurueck),
    [original, entwurf, bisherZurueck],
  );
  const summeCent = ergebnis?.ok === true ? ergebnis.summeCent : null;

  const oeffne = useCallback(
    async (belegnummer: string): Promise<void> => {
      setLaedt(true);
      setSuchMeldung(null);
      try {
        const beleg = await api.beleg(kassenId, belegnummer);
        let storno: string | null = null;
        if (beleg.belegtyp === 'STANDARD') {
          const j = await api.belegjournal(kassenId, {
            suche: `ST-${beleg.belegnummer}`,
            limit: 100,
          });
          storno = stornoAusJournal(beleg, j.belege);
        }
        const positionen = beleg.positionen.map((p): OriginalPosition => ({
          bezeichnung: p.bezeichnung,
          menge: p.menge,
          bruttoCent: p.bruttoCent,
          steuersatz: p.steuersatz,
          artikelId: p.artikelId ?? null,
        }));
        const auswertung = effektiveZeilen(positionen);
        setOriginal({
          beleg,
          storno,
          pruefung: pruefeOriginal({
            belegtyp: beleg.belegtyp,
            sonderbeleg: beleg.sonderbeleg,
            zahlungsart: beleg.zahlungsart,
            gutscheinCent: beleg.gutscheinCent,
            storniert: storno !== null,
          }),
          zeilen: auswertung.zeilen,
          // M1: Original mit Leergut/Rücknahme/Abzug/Gutscheineinlösung ganz gesperrt
          fehler: [...auswertung.fehler, ...negativeZeilenSperre(auswertung.zeilen)],
          positionen,
        });
        setEntwurf(LEERER_ENTWURF);
        setTreffer(null);
      } catch (e: unknown) {
        setOriginal(null);
        setSuchMeldung(
          e instanceof ApiFehler && e.status === 404
            ? `Beleg ${belegnummer} wurde an dieser Kasse nicht gefunden.`
            : 'Beleg konnte nicht geladen werden – Retouren nur online. Bitte erneut versuchen.',
        );
      } finally {
        setLaedt(false);
      }
    },
    [api, kassenId],
  );

  async function suche(): Promise<void> {
    const e = deuteEingabe(eingabe);
    setSuchMeldung(null);
    switch (e.art) {
      case 'leer':
        setSuchMeldung('Bitte Beleg scannen oder Belegnummer bzw. Suchbegriff eingeben.');
        return;
      case 'qr-ungueltig':
        setSuchMeldung('Der gescannte Code ist kein gültiger Beleg-QR-Code.');
        return;
      case 'qr':
        if (e.kassenId !== kassenId) {
          // keine Anfrage an fremde Kassen – kassenübergreifend erst mit RU-2
          setSuchMeldung(
            'Dieser Beleg stammt nicht von dieser Kasse. In Stufe 1 ist die Rückgabe nur an der Kasse des Originals vorbereitbar.',
          );
          return;
        }
        await oeffne(e.belegnummer);
        return;
      case 'nummer':
        await oeffne(e.belegnummer);
        return;
      case 'betrag':
      case 'text': {
        setLaedt(true);
        try {
          const r = await api.belegjournal(kassenId, {
            ...(e.art === 'text' ? { suche: e.text } : {}),
            limit: e.art === 'betrag' ? 200 : 50,
          });
          const liste = r.belege
            .filter(istVerkaufsbeleg)
            .filter((b) => e.art !== 'betrag' || b.summeCent === e.cent);
          setTreffer(liste);
          if (liste.length === 0) {
            setSuchMeldung(
              e.art === 'betrag'
                ? `Kein Verkaufsbeleg über ${euro(e.cent)} unter den letzten 200 Belegen dieser Kasse.`
                : `Keine Verkaufsbelege für „${e.text}“.`,
            );
          }
        } catch {
          setTreffer(null);
          setSuchMeldung('Suche nicht möglich – Retouren nur online.');
        } finally {
          setLaedt(false);
        }
      }
    }
  }

  function neueRetoure(): void {
    setOriginal(null);
    setEntwurf(LEERER_ENTWURF);
    setTreffer(null);
    setEingabe('');
    setSuchMeldung(null);
    setSchritt('beleg');
  }

  const bezeichnung = (index: number): string =>
    original?.zeilen[index]?.bezeichnung ?? `Position ${String(index + 1)}`;
  const fehlend = fehlendeAngaben(entwurf, bezeichnung);
  const auswahl = auswahlListe(entwurf);

  // Darf der Bediener zum nächsten Schritt? (Abschluss selbst ist immer gesperrt.)
  const weiterMoeglich: Record<Schritt, boolean> = {
    beleg: original !== null && original.pruefung.rueckgabefaehig && original.fehler.length === 0,
    artikel: ergebnis?.ok === true,
    grund: ergebnis?.ok === true && fehlend.length === 0,
    erstattung: ergebnis?.ok === true && fehlend.length === 0,
    abschluss: false,
  };
  const reihenfolge = SCHRITTE.map((s) => s.id);
  const aktuell = reihenfolge.indexOf(schritt);

  // Codex Runde 2 (M2b): aktuelle Werte für die Prüfung nach dem await (nicht die eingefangenen)
  const aktuellerStand = useRef({ online, darf, kassenSperre, schritt, entwurf });
  aktuellerStand.current = { online, darf, kassenSperre, schritt, entwurf };
  /**
   * Codex Runde 3 (1): Zustandsrevision – steigt bei jeder Änderung von online, Recht,
   * Kassensperre, Schritt oder Entwurf. Ein Hin-und-zurück-Wechsel während des Abrufs
   * (null → STOP → null) ändert die Revision und verwirft den Schritt.
   */
  const revision = useRef(0);
  useLayoutEffect(() => {
    revision.current++;
  }, [online, darf, kassenSperre, schritt, entwurf]);

  async function weiter(): Promise<void> {
    // synchron gegen gleichzeitige Weiter-Aktionen (Doppeltipp)
    if (schrittPruefung.current !== null) {
      return;
    }
    const naechster = reihenfolge[aktuell + 1];
    if (naechster === undefined || !weiterMoeglich[schritt] || gesperrt) {
      return;
    }
    const meine = ++schrittKennung.current;
    schrittPruefung.current = meine;
    setPruefeSchritt(true);
    const revisionVorher = revision.current;
    try {
      // M2: vor jedem Schritt den Kassenstatus frisch prüfen (Ausfall mitten im Ablauf)
      const stand = await statusPruefen(true);
      const jetzt = aktuellerStand.current;
      // verwerfen: abgebrochene oder überholte Prüfung, jede Zustandsänderung während des
      // Abrufs (auch hin und zurück), offline, ohne Recht oder gesperrt
      if (
        schrittPruefung.current !== meine ||
        stand === null ||
        revision.current !== revisionVorher ||
        !jetzt.online ||
        !jetzt.darf ||
        systemSperre(stand, jetzt.kassenSperre, Date.now()) !== null
      ) {
        return;
      }
      setSchritt(naechster);
    } finally {
      // nur die eigene, nicht abgebrochene Prüfung gibt die Sperre frei
      if (schrittPruefung.current === meine) {
        schrittPruefung.current = null;
        setPruefeSchritt(false);
      }
    }
  }

  function zurueck(): void {
    const voriger = reihenfolge[aktuell - 1];
    if (voriger !== undefined) {
      setSchritt(voriger);
    } else if (original !== null) {
      neueRetoure();
    }
  }

  const weiterText: Record<Schritt, string> = {
    beleg: 'Weiter: Artikel',
    artikel: 'Weiter: Grund',
    grund: 'Weiter: Erstattung',
    erstattung: 'Weiter: Abschluss',
    abschluss: 'Retoure abschließen',
  };

  return (
    <div className="retoure">
      <nav className="rt-schritte" aria-label="Schritte der Retoure">
        <ol>
          {SCHRITTE.map((s, i) => {
            const erledigt = i < aktuell;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  className={i === aktuell ? 'aktiv' : erledigt ? 'erledigt' : ''}
                  aria-current={i === aktuell ? 'step' : undefined}
                  disabled={!erledigt}
                  onClick={() => {
                    setSchritt(s.id);
                  }}
                >
                  {erledigt ? '✓' : String(i + 1)} {s.text}
                </button>
              </li>
            );
          })}
        </ol>
        <span className="rt-entwurf-marke">Vorschau – Stufe 1 nicht freigegeben</span>
      </nav>

      {!online && (
        <div className="karte rt-sperre" role="alert">
          Retoure nur online möglich. Ohne Verbindung sind alle Aktionen gesperrt.
        </div>
      )}
      {online && sperrgrund !== null && (
        <div className="karte rt-sperre" role="alert">
          {sperrgrund}
        </div>
      )}
      {online && sperrgrund === null && !darf && (
        <div className="karte rt-sperre" role="alert">
          Kein Recht für Retouren: Der angemeldete Bediener braucht das Stornorecht (Vorbereitung
          bis zur Freigabe eigener Retourenrechte).
        </div>
      )}

      <div className="rt-raster">
        <section className="karte rt-arbeit" aria-labelledby="rt-ueberschrift">
          {schritt === 'beleg' && (
            <>
              <h2 id="rt-ueberschrift" ref={ueberschrift} tabIndex={-1}>
                {original === null ? 'Originalbeleg finden' : 'Originalbeleg prüfen'}
              </h2>
              {original === null ? (
                <>
                  <div className="rt-einstieg">
                    {[
                      {
                        titel: 'Beleg-QR scannen',
                        text: 'QR-Code am Kassenbon mit dem Handscanner',
                        marke: 'empfohlen',
                      },
                      { titel: 'Belegnummer eingeben', text: 'z. B. B-20261018-XRHU-0042' },
                      { titel: 'In Belegen suchen', text: 'Artikel oder Betrag – diese Kasse' },
                    ].map((k) => (
                      <button
                        key={k.titel}
                        type="button"
                        className="rt-kachel"
                        disabled={gesperrt}
                        onClick={() => {
                          suchfeld.current?.focus();
                        }}
                      >
                        {k.marke !== undefined && <span className="rt-marke">{k.marke}</span>}
                        <strong>{k.titel}</strong>
                        <small>{k.text}</small>
                      </button>
                    ))}
                  </div>
                  <div className="zeile rt-suche">
                    <label>
                      Beleg scannen, Belegnummer, Artikel oder Betrag
                      <input
                        ref={suchfeld}
                        value={eingabe}
                        disabled={gesperrt}
                        autoComplete="off"
                        spellCheck={false}
                        onChange={(ev) => {
                          setEingabe(ev.target.value);
                        }}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter') {
                            ev.preventDefault();
                            void suche();
                          }
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      className="taste"
                      disabled={gesperrt || laedt}
                      onClick={() => {
                        void suche();
                      }}
                    >
                      Suchen
                    </button>
                  </div>
                  {suchMeldung !== null && (
                    <p className="fehler" role="alert">
                      {suchMeldung}
                    </p>
                  )}
                  {treffer !== null && treffer.length > 0 && (
                    <table className="liste rt-treffer">
                      <caption className="unsichtbar">Gefundene Verkaufsbelege</caption>
                      <thead>
                        <tr>
                          <th>Beleg</th>
                          <th>Datum</th>
                          <th>Zahlung</th>
                          <th className="betrag">Betrag</th>
                        </tr>
                      </thead>
                      <tbody>
                        {treffer.map((t) => (
                          <tr key={t.belegnummer}>
                            <td>
                              <button
                                type="button"
                                className="taste zweit"
                                disabled={gesperrt || laedt}
                                onClick={() => {
                                  void oeffne(t.belegnummer);
                                }}
                              >
                                {t.belegnummer}
                              </button>
                            </td>
                            <td>{datum(t.zeitpunkt)}</td>
                            <td>{t.zahlungsart ?? '–'}</td>
                            <td className="betrag">{euro(t.summeCent)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div className="rt-historie">
                    <h3>Heute an dieser Kasse</h3>
                    <p className="hinweis">
                      Retourenbelege: noch keine – der Abschluss ist nicht freigegeben (Historie ab
                      RU-1/RU-3).
                    </p>
                    {storniHeute !== null && storniHeute.length > 0 && (
                      <ul aria-label="Letzte Storni dieser Kasse (bisheriger Weg)">
                        {storniHeute.map((s) => (
                          <li key={s.belegnummer}>
                            <span>
                              {s.belegnummer} <small>{datum(s.zeitpunkt)} · Storno</small>
                            </span>
                            <strong>{euro(s.summeCent)}</strong>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <p className="hinweis rt-punkt">
                    Stufe 1: Rückgabe mit Originalbeleg, Erstattung bar, nur online. Der
                    Originalbeleg bleibt unverändert; jede Rückgabe bekäme einen eigenen, signierten
                    Beleg.
                  </p>
                </>
              ) : (
                <OriginalPruefen original={original} />
              )}
            </>
          )}

          {schritt === 'artikel' && original !== null && (
            <>
              <h2 id="rt-ueberschrift" ref={ueberschrift} tabIndex={-1}>
                Artikel auswählen
              </h2>
              <PositionsAuswahl
                original={original}
                bisherZurueck={bisherZurueck}
                entwurf={entwurf}
                gesperrt={gesperrt}
                onMenge={(index, menge) => {
                  setEntwurf((e) => setzeMenge(e, index, menge));
                }}
              />
              {ergebnis?.ok === false && (
                <ul className="fehler" role="alert">
                  {ergebnis.fehler.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              )}
              <p className="hinweis rt-punkt">
                Beträge stammen aus dem Originalbeleg (Preis, Rabatte, Steuersatz), nicht aus dem
                aktuellen Artikelstamm. „Zurück“ kennt in Stufe 1 nur vorhandene Storni;
                Teilretouren werden erst mit RU-1 gezählt.
              </p>
            </>
          )}

          {schritt === 'grund' && original !== null && (
            <>
              <h2 id="rt-ueberschrift" ref={ueberschrift} tabIndex={-1}>
                Grund und Zustand erfassen
              </h2>
              {auswahl.map(({ index, menge }) => {
                const a = entwurf.angaben.get(index) ?? LEERE_ANGABE;
                const id = `rt-pos-${String(index)}`;
                return (
                  <fieldset key={index} className="rt-angaben" aria-labelledby={id}>
                    <h3 id={id}>
                      {bezeichnung(index)} · {mengeText(menge)} Stück
                    </h3>
                    <div className="chips" role="group" aria-label="Rückgabegrund">
                      {GRUENDE.map((g) => (
                        <button
                          key={g.id}
                          type="button"
                          className={a.grund === g.id ? 'aktiv' : ''}
                          aria-pressed={a.grund === g.id}
                          disabled={gesperrt}
                          onClick={() => {
                            setEntwurf((e) => setzeAngabe(e, index, { grund: g.id }));
                          }}
                        >
                          {g.text}
                        </button>
                      ))}
                    </div>
                    {a.grund === 'mangel' && (
                      <p className="hinweis rt-kasten">
                        Gesetzliche Gewährleistung – Frist und Warengruppen-Ausschluss gelten nicht,
                        Freigabe durch die Filialleitung nötig, Ware geht „Zur Prüfung“.
                      </p>
                    )}
                    <label>
                      Notiz {a.grund === 'sonstiges' ? '(Pflicht bei „Sonstiges“)' : '(optional)'}
                      <input
                        value={a.notiz}
                        maxLength={200}
                        disabled={gesperrt}
                        onChange={(ev) => {
                          const notiz = ev.target.value;
                          setEntwurf((e) => setzeAngabe(e, index, { notiz }));
                        }}
                      />
                    </label>
                    <div className="rt-zustaende" role="radiogroup" aria-label="Artikelzustand">
                      {ZUSTAENDE.map((z) => {
                        const waehlbar = zustandWaehlbar(z.id, a.grund);
                        return (
                          <label key={z.id} className={waehlbar ? '' : 'rt-aus'}>
                            <input
                              type="radio"
                              name={`rt-zustand-${String(index)}`}
                              checked={a.zustand === z.id}
                              disabled={gesperrt || !waehlbar}
                              onChange={() => {
                                setEntwurf((e) => setzeAngabe(e, index, { zustand: z.id }));
                              }}
                            />
                            <span>{z.text}</span>
                            <small>{z.stufe1 ? z.wirkung : 'Ausbaustufe später'}</small>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                );
              })}
              {fehlend.length > 0 && (
                <ul className="hinweis">
                  {fehlend.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              )}
            </>
          )}

          {schritt === 'erstattung' && original !== null && ergebnis?.ok === true && (
            <>
              <h2 id="rt-ueberschrift" ref={ueberschrift} tabIndex={-1}>
                Rückerstattungsbetrag und Zahlungsart
              </h2>
              <div className="rt-kasten">
                <h3>Erstattungsbetrag (aus dem Originalbeleg)</h3>
                <table className="liste">
                  <tbody>
                    {ergebnis.zeilen.map((z) => (
                      <tr key={z.index}>
                        <td>
                          {z.bezeichnung} · {mengeText(z.menge)} von{' '}
                          {mengeText(original.zeilen[z.index]?.menge ?? 0)} ·{' '}
                          {satzText(z.steuersatz)}
                        </td>
                        <td className="betrag">{euro(z.ohneRabattCent)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td>anteiliger Bon-Rabatt</td>
                      <td className="betrag">
                        {euro(ergebnis.zeilen.reduce((s, z) => s + z.rabattAnteilCent, 0))}
                      </td>
                    </tr>
                    <tr className="rt-gesamt">
                      <th scope="row">Erstattung</th>
                      <td className="betrag">{euro(ergebnis.summeCent)}</td>
                    </tr>
                  </tbody>
                </table>
                <table className="liste rt-saetze">
                  <caption>Je Steuersatz (Retourenbeleg weist die Sätze getrennt aus)</caption>
                  <thead>
                    <tr>
                      <th>Satz</th>
                      <th className="betrag">Brutto</th>
                      <th className="betrag">Netto</th>
                      <th className="betrag">USt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ergebnis.jeSteuersatz.map((s) => (
                      <tr key={s.steuersatz}>
                        <td>{satzText(s.steuersatz)}</td>
                        <td className="betrag">{euro(s.bruttoCent)}</td>
                        <td className="betrag">{euro(s.nettoCent)}</td>
                        <td className="betrag">{euro(s.ustCent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <h3>Erstattungsart</h3>
              <div className="rt-arten" role="radiogroup" aria-label="Erstattungsart">
                <label className="rt-art gewaehlt">
                  <input type="radio" name="rt-art" checked readOnly disabled={gesperrt} />
                  <strong>Bar</strong>
                  <small>wie im Original · Lade öffnet erst nach dem Beleg</small>
                </label>
                {[
                  { text: 'Karte', unter: 'PAYONE/ZVT-Erstattung' },
                  { text: 'Gutschein', unter: 'Gutschein / Gutschrift' },
                ].map((a) => (
                  <label key={a.text} className="rt-art rt-aus">
                    <input type="radio" name="rt-art" disabled />
                    <strong>{a.text}</strong>
                    <small>{a.unter}</small>
                    <span className="rt-marke warnung">Ausbaustufe später</span>
                  </label>
                ))}
              </div>
              <FreigabeHinweise entwurf={entwurf} summeCent={ergebnis.summeCent} />
            </>
          )}

          {schritt === 'abschluss' && original !== null && ergebnis?.ok === true && (
            <>
              <h2 id="rt-ueberschrift" ref={ueberschrift} tabIndex={-1}>
                Retoure abschließen?
              </h2>
              <dl className="rt-uebersicht">
                <dt>Original</dt>
                <dd>
                  {original.beleg.belegnummer} ({datum(original.beleg.zeitpunkt)})
                </dd>
                <dt>Rückgabe</dt>
                <dd>
                  {ergebnis.zeilen
                    .map(
                      (z) =>
                        `${z.bezeichnung} · ${mengeText(z.menge)} von ${mengeText(original.zeilen[z.index]?.menge ?? 0)}`,
                    )
                    .join('; ')}
                </dd>
                <dt>Grund · Zustand</dt>
                <dd>
                  {auswahl
                    .map(({ index }) => {
                      const a = entwurf.angaben.get(index) ?? LEERE_ANGABE;
                      return `${GRUENDE.find((g) => g.id === a.grund)?.text ?? '–'} · ${ZUSTAENDE.find((z) => z.id === a.zustand)?.text ?? '–'}`;
                    })
                    .join('; ')}
                </dd>
                <dt>Erstattung</dt>
                <dd>{euro(ergebnis.summeCent)} bar</dd>
                <dt>Retourenbeleg (Vorschau)</dt>
                <dd>
                  Belegtyp offen (RF-1);{' '}
                  {ergebnis.jeSteuersatz
                    .map((s) => `${satzText(s.steuersatz)}: ${euro(-s.bruttoCent)}`)
                    .join(', ')}
                </dd>
              </dl>
              <div className="rt-kasten">
                <strong>Was passieren würde</strong>
                <p>
                  Der Server signiert den Retourenbeleg. Danach öffnet die Lade –{' '}
                  {euro(ergebnis.summeCent)} auszahlen. Lagerbuchung nach Zustand.
                </p>
                <p className="warnung" id="rt-abschluss-sperre">
                  {ABSCHLUSS_GESPERRT_TEXT}. In diesem Stand wird nichts gebucht, nichts signiert
                  und nichts ausgezahlt.
                </p>
              </div>
            </>
          )}
        </section>

        <aside className="rt-seite" aria-label="Zusammenfassung der Rückgabe">
          <div className="karte rt-zusammenfassung">
            <div className="rt-betragsband">
              <span>Erstattung bar · Vorschau</span>
              <strong aria-live="polite">{summeCent === null ? '—' : euro(summeCent)}</strong>
            </div>
            <div className="rt-zusammenfassung-inhalt">
              <h3>Rückgabe</h3>
              {original === null ? (
                <>
                  <p className="hinweis">Retouren nur online</p>
                  <p className="hinweis">
                    Noch keine Rückgabe erfasst. Der Originalbeleg bleibt unverändert.
                  </p>
                </>
              ) : (
                <>
                  <p className="hinweis">zu {original.beleg.belegnummer}</p>
                  {ergebnis?.ok === true && (
                    <ul className="rt-zeilen">
                      {ergebnis.zeilen.map((z) => (
                        <li key={z.index}>
                          <span>
                            {z.bezeichnung}
                            <small>{mengeText(z.menge)} Stück</small>
                          </span>
                          <strong className="rt-negativ">{euro(-z.erstattungCent)}</strong>
                        </li>
                      ))}
                    </ul>
                  )}
                  {aktuell >= 3 && <p className="rt-zeile-klein">Erstattung: bar</p>}
                </>
              )}
            </div>
          </div>
          <div className="rt-aktionen">
            {schritt === 'abschluss' ? (
              <>
                <button
                  type="button"
                  className="taste gross rt-abschluss"
                  disabled
                  aria-describedby="rt-abschluss-hinweis"
                >
                  Retoure abschließen
                </button>
                <p id="rt-abschluss-hinweis" className="warnung">
                  {ABSCHLUSS_GESPERRT_TEXT}
                </p>
                <button type="button" className="taste zweit gross" onClick={neueRetoure}>
                  Neue Retoure
                </button>
              </>
            ) : (
              <button
                type="button"
                className="taste gross"
                disabled={gesperrt || !weiterMoeglich[schritt]}
                onClick={() => {
                  void weiter();
                }}
              >
                {weiterText[schritt]}
              </button>
            )}
            <button
              type="button"
              className="taste zweit gross"
              disabled={schritt === 'beleg' && original === null}
              onClick={zurueck}
            >
              Zurück
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}

function OriginalPruefen(props: { original: Original }) {
  const { beleg, pruefung, storno, fehler } = props.original;
  const summe = beleg.positionen.reduce((s, p) => s + p.bruttoCent, 0);
  return (
    <div className="rt-kasten">
      <dl className="rt-uebersicht">
        <dt>Beleg</dt>
        <dd>{beleg.belegnummer}</dd>
        <dt>Datum</dt>
        <dd>{datum(beleg.zeitpunkt)}</dd>
        <dt>Zahlungsart</dt>
        <dd>{beleg.zahlungsart ?? '–'}</dd>
        <dt>Summe</dt>
        <dd>{euro(summe)}</dd>
        <dt>Positionen</dt>
        <dd>{String(beleg.positionen.length)}</dd>
        {storno !== null && (
          <>
            <dt>Storno</dt>
            <dd>{storno}</dd>
          </>
        )}
      </dl>
      {pruefung.rueckgabefaehig && fehler.length === 0 ? (
        <p className="ok">Original geprüft – Rückgabe kann vorbereitet werden.</p>
      ) : (
        <ul className="fehler" role="alert">
          {[...pruefung.gruende, ...fehler.map((f) => `Klärungsfall: ${f}`)].map((g) => (
            <li key={g}>{g}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PositionsAuswahl(props: {
  original: Original;
  bisherZurueck: ReadonlyMap<number, number>;
  entwurf: Entwurf;
  gesperrt: boolean;
  onMenge: (index: number, menge: number) => void;
}) {
  const { original, bisherZurueck, entwurf, gesperrt, onMenge } = props;
  const sichtbar = original.zeilen.filter((z) => z.art !== 'rabatt');
  const rabatt = original.zeilen.some((z) => z.art === 'rabatt');
  return (
    <>
      {/* Browser-Nachweis 1024 × 768: notfalls scrollt die Tabelle in der Karte, nie die Seite */}
      <div className="rt-tabelle">
        <table className="liste rt-positionen">
          <caption className="unsichtbar">Positionen des Originalbelegs</caption>
          <thead>
            <tr>
              <th aria-label="Auswahl" />
              <th>Artikel</th>
              <th className="betrag rt-spalte-menge">gekauft</th>
              <th className="betrag rt-spalte-menge">zurück</th>
              <th className="betrag rt-spalte-menge">möglich</th>
              <th>Rückgabe</th>
              <th className="betrag rt-betrag">Betrag</th>
            </tr>
          </thead>
          <tbody>
            {sichtbar.map((z) => {
              const zurueck = bisherZurueck.get(z.index) ?? 0;
              const moeglich = z.sperrgrund === null ? z.menge - zurueck : 0;
              const menge = entwurf.auswahl.get(z.index) ?? 0;
              const aus = gesperrt || moeglich <= 0;
              const schritt = z.nurGanz ? z.menge : 1;
              const betrag =
                menge > 0
                  ? kumulativerBetrag(z.effektivCent, z.menge, zurueck + menge) -
                    kumulativerBetrag(z.effektivCent, z.menge, zurueck)
                  : null;
              const stueck = Number.isInteger(z.menge)
                ? `${euro(kumulativerBetrag(z.bruttoCent, z.menge, 1))} je Stück`
                : `${mengeText(z.menge)} · ${euro(z.bruttoCent)}`;
              return (
                <tr key={z.index} className={moeglich <= 0 ? 'rt-aus' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`${z.bezeichnung} zurückgeben`}
                      checked={menge > 0}
                      disabled={aus}
                      onChange={(ev) => {
                        onMenge(z.index, ev.target.checked ? schritt : 0);
                      }}
                    />
                  </td>
                  <td className="rt-bezeichnung">
                    <strong>{z.bezeichnung}</strong>
                    <small>
                      {z.sperrgrund ?? `${satzText(z.steuersatz)} · ${stueck}`}
                      {z.sperrgrund === null && z.rabattAnteilCent !== 0 && ' · mit Bon-Rabatt'}
                    </small>
                    {/* kompakt (bis 1199 px): Mengen in der Unterzeile statt eigener Spalten */}
                    <small className="rt-nur-schmal">
                      gekauft {mengeText(z.menge)} · zurück {mengeText(zurueck)} · möglich{' '}
                      {mengeText(moeglich)}
                    </small>
                  </td>
                  <td className="betrag rt-spalte-menge">{mengeText(z.menge)}</td>
                  <td className="betrag rt-spalte-menge">{mengeText(zurueck)}</td>
                  <td className="betrag rt-spalte-menge">{mengeText(moeglich)}</td>
                  <td>
                    <span className="stepper rt-stepper">
                      <button
                        type="button"
                        aria-label={`${z.bezeichnung}: eins weniger`}
                        disabled={aus || menge <= 0}
                        onClick={() => {
                          onMenge(z.index, Math.max(0, menge - schritt));
                        }}
                      >
                        −
                      </button>
                      <span aria-live="polite">
                        {mengeText(menge)} von {mengeText(moeglich)}
                      </span>
                      <button
                        type="button"
                        aria-label={`${z.bezeichnung}: eins mehr`}
                        disabled={aus || menge + schritt > moeglich}
                        onClick={() => {
                          onMenge(z.index, menge + schritt);
                        }}
                      >
                        +
                      </button>
                    </span>
                  </td>
                  <td className="betrag rt-betrag">
                    {betrag === null ? '–' : <span className="rt-negativ">{euro(-betrag)}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rabatt && (
        <p className="hinweis">
          Bon-Rabatt des Originals ist anteilig auf die Artikel verteilt (größter Rest, je
          Steuersatz).
        </p>
      )}
    </>
  );
}

function FreigabeHinweise(props: { entwurf: Entwurf; summeCent: number }) {
  const h = freigabeHinweise(props.entwurf, props.summeCent);
  return h.length === 0 ? (
    <p className="hinweis rt-punkt">
      Freigabe nicht nötig: Erstattung unter der Grenze von 50,00 € (Vorschlag E-8).
    </p>
  ) : (
    <ul className="warnung">
      {h.map((x) => (
        <li key={x}>{x} – Freigabe per PIN ist eine spätere Ausbaustufe (RU-5).</li>
      ))}
    </ul>
  );
}
